import React, { useEffect, useState } from "react";
import { Badge, Center, Group, Loader, Paper, SimpleGrid, Stack, Text } from "@mantine/core";
import { getOrganizationsOfProductIds } from "@utils/api-communication/RestApiCli";
import { mapControllerConnectionState } from "@utils/controllerBatteryUtils";
import { ControllerConnectionState } from "types/productTypes";
import { IProduct } from "types/productManagementTypes";
import { Organization } from "types/orgTypes";
import ConfigJsonView from "./ConfigJsonView";
import DeviceLogs from "./DeviceLogs";
import { useDeviceConfigs } from "./useDeviceConfigs";
import { onOff, plain, timeoutSeconds } from "./DeviceStatesPanel";

type Row = { label: string; value: React.ReactNode; hint?: string };

const dateValue = (value?: Date | string | null): string =>
  value ? new Date(value).toLocaleString() : "-";

// Unreported stays neutral rather than reading as a denial.
const onOffChip = (value: unknown): React.ReactNode => {
  const label = onOff(value);
  if (label === "On")
    return (
      <Badge variant="light" color="green">
        On
      </Badge>
    );
  if (label === "Off")
    return (
      <Badge variant="light" color="red">
        Off
      </Badge>
    );
  return (
    <Text size="sm" c="dimmed">
      {label}
    </Text>
  );
};

// A bitmask; see Product.controllers in the backend schema.
const CONTROLLER_LABELS: Record<ControllerConnectionState, string> = {
  [ControllerConnectionState.None]: "Both disconnected",
  [ControllerConnectionState.LeftOnly]: "Left connected",
  [ControllerConnectionState.RightOnly]: "Right connected",
  [ControllerConnectionState.Both]: "Both connected",
  [ControllerConnectionState.Error]: "Error",
};

export const controllersLabel = (value?: number | null): string => {
  if (value === undefined || value === null) return "-";
  const state = mapControllerConnectionState(value);
  return state ? `${CONTROLLER_LABELS[state]} (${value})` : String(value);
};

export const controllerBatteryLabel = (value?: number | null): string =>
  typeof value === "number" ? `${value}%` : "Not reported";

const ExplainedSection: React.FC<{
  title: string;
  description: string;
  rows?: Row[];
  children?: React.ReactNode;
}> = ({ title, description, rows, children }) => (
  <Paper withBorder radius="sm" p="md">
    <Stack gap="xs">
      <Stack gap={2}>
        <Text fw={600} size="sm">
          {title}
        </Text>
        <Text size="xs" c="dimmed">
          {description}
        </Text>
      </Stack>
      {rows && (
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
          {rows.map(({ label, value, hint }) => (
            <Group key={label} gap="xs" wrap="nowrap" align="flex-start">
              <Text size="sm" c="dimmed" w={170} style={{ flexShrink: 0 }}>
                {label}
              </Text>
              <Stack gap={0}>
                {typeof value === "string" || typeof value === "number" ? (
                  <Text size="sm" style={{ wordBreak: "break-word" }}>
                    {value}
                  </Text>
                ) : (
                  value
                )}
                {hint && (
                  <Text size="xs" c="dimmed">
                    {hint}
                  </Text>
                )}
              </Stack>
            </Group>
          ))}
        </SimpleGrid>
      )}
      {children}
    </Stack>
  </Paper>
);

interface ProductDetailsPanelProps {
  product: IProduct;
}

const ProductDetailsPanel: React.FC<ProductDetailsPanelProps> = ({ product }) => {
  const [organization, setOrganization] = useState<Organization | null>(null);

  const pid = product.uuid;
  const { applyConfig: deviceConfig, loading: configLoading } = useDeviceConfigs(pid);
  useEffect(() => {
    if (!pid) return;
    let cancelled = false;
    setOrganization(null);

    getOrganizationsOfProductIds(
      pid,
      (response) => {
        if (cancelled) return;
        const orgData =
          Array.isArray(response.data) && response.data.length > 0 ? response.data[0] : null;
        setOrganization(orgData);
      },
      (error) => {
        if (cancelled) return;
        console.error("Failed to fetch organization info", error);
        setOrganization(null);
      },
    );

    return () => {
      cancelled = true;
    };
  }, [pid]);

  const states = (product.states ?? {}) as Record<string, unknown>;
  const playerPrefs = (states.playerPrefs ?? {}) as Record<string, unknown>;
  const permissions = (states.permissions ?? {}) as Record<string, unknown>;
  const storedConfig = states.managementConfig;

  return (
    <Stack gap="md">
      <ExplainedSection
        title="Identity"
        description="How this headset is identified across the platform. The PID is the device-specific code the headset reports on connect."
        rows={[
          { label: "PID (uuid)", value: plain(product.uuid) },
          { label: "Alias", value: plain(product.alias) },
          { label: "Model", value: plain(states.model ?? product.model) },
          { label: "Serial number", value: plain(states.serialNo) },
          { label: "Device specific code", value: plain(states.deviceSpecificCode) },
        ]}
      />

      <ExplainedSection
        title="Software versions"
        description="App version is the Oculera headset build; PUI is the PICO system UI version the headset runs."
        rows={[
          { label: "App version", value: plain(product.version) },
          { label: "PUI version", value: plain(states.pui ?? product.puiVersion) },
        ]}
      />

      <ExplainedSection
        title="Connectivity"
        description="When each side last talked to the backend. The scene is where the headset app was the last time it reported in."
        rows={[
          {
            label: "Headset last connected",
            value: dateValue(product.vrDate),
            hint: "vrDate — last time the headset socket connected",
          },
          {
            label: "Web last connected",
            value: dateValue(product.webDate),
            hint: "webDate — last time a web client connected for this device",
          },
          {
            label: "Last access",
            value: dateValue(product.lastAccess),
            hint: "Most recent interaction from either side",
          },
          {
            label: "Scene",
            value: plain(product.scene),
            hint: "Menu or Test — where the headset app was",
          },
        ]}
      />

      <ExplainedSection
        title="Power & controllers"
        description="Battery and controller state as last reported by the headset app. Controllers is a bitmask: 0 none, 1 left, 2 right, 3 both."
        rows={[
          { label: "Headset battery", value: product.battery ? `${product.battery}%` : "-" },
          { label: "Volume", value: plain(product.volume) },
          {
            label: "Controllers connected",
            value: controllersLabel(product.controllers),
            hint: "Also reported inside the device state as controllerState",
          },
          {
            label: "Left controller battery",
            value: controllerBatteryLabel(product.controllerBattery?.left),
          },
          {
            label: "Right controller battery",
            value: controllerBatteryLabel(product.controllerBattery?.right),
          },
        ]}
      />

      <ExplainedSection
        title="Reported device state"
        description="The headset's own system state when it last connected (GetAllStates on the device). These are the actual values on the hardware, not what the platform asked for."
        rows={[
          { label: "Device IP", value: plain(states.deviceIp) },
          { label: "Wi-Fi SSID", value: plain(states.wifi_ssid) },
          { label: "Brightness", value: plain(states.brightness) },
          { label: "Controller state", value: controllersLabel(Number(states.controllerState)) },
          { label: "Sleep delay", value: timeoutSeconds(states.sleepDelay) },
          { label: "Screen-off delay", value: timeoutSeconds(states.screenOffDelay) },
          { label: "USB debugging", value: onOff(states.usbDebugging) },
          { label: "System update", value: onOff(states.systemUpdate) },
          { label: "Wi-Fi always on", value: onOff(states.wifiAlwaysOn) },
          { label: "Play boundary", value: onOff(states.playBoundary) },
          { label: "App as home", value: plain(states.appAsHome) },
        ]}
      />

      {Object.keys(permissions).length > 0 && (
        <ExplainedSection
          title="Device permissions"
          description="Android permissions the headset app has been granted on this device."
          rows={[
            { label: "Eye tracking", value: onOffChip(permissions.eyeTracking) },
            { label: "Write external storage", value: onOffChip(permissions.writeExternalStorage) },
            ...("writeSettings" in permissions
              ? [{ label: "Write settings", value: onOffChip(permissions.writeSettings) }]
              : []),
          ]}
        />
      )}

      <ExplainedSection
        title="Player prefs on the headset"
        description="The last values saved on the device (Unity PlayerPrefs, so they arrive as strings)."
        rows={[
          { label: "Logger enabled", value: plain(playerPrefs.enableLogger) },
          {
            label: "API config key",
            value: plain(playerPrefs.configKey),
            hint: "Empty means the device uses the default backend",
          },
          {
            label: "Keys disabled on test",
            value: plain(playerPrefs.disableKeyConfigOnTest),
            hint: "When true the headset disables its home key while a test runs",
          },
        ]}
      />

      <ExplainedSection
        title="Management config stored on the headset"
        description="The managementConfig.json the headset last wrote to disk. This is what the device is actually running, so it can differ from the config the platform has queued."
      >
        {storedConfig ? (
          <ConfigJsonView value={storedConfig} />
        ) : (
          <Text size="sm" c="dimmed">
            The device has not reported a stored management config yet.
          </Text>
        )}
      </ExplainedSection>

      <ExplainedSection
        title="Saved config on the platform"
        description="What the backend has stored for this device. Applied turns true once the headset confirms it took the config; until then it is pending."
      >
        {configLoading ? (
          <Center my="md">
            <Loader size="sm" />
          </Center>
        ) : deviceConfig?.config ? (
          <Stack gap="xs">
            <Group gap="xs">
              <Text size="sm" c="dimmed">
                Type
              </Text>
              <Text size="sm">{deviceConfig.type || "-"}</Text>
              <Text size="sm" c="dimmed" ml="md">
                Applied
              </Text>
              <Text size="sm">{deviceConfig.applied ? "Yes" : "No"}</Text>
              <Text size="sm" c="dimmed" ml="md">
                Date
              </Text>
              <Text size="sm">{dateValue(deviceConfig.date)}</Text>
            </Group>
            <ConfigJsonView value={deviceConfig.config} />
          </Stack>
        ) : (
          <Text size="sm" c="dimmed">
            No saved config for this device. It will fall back to the matching default config.
          </Text>
        )}
      </ExplainedSection>

      <ExplainedSection
        title="Organization"
        description="The organization this device is assigned to."
      >
        {organization ? (
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
            {(
              [
                ["Org ID", plain(organization.orgId)],
                ["Name", plain(organization.name)],
                ["Email", plain(organization.email)],
                ["Country", plain(organization.country)],
                ["Scope", plain(organization.scopeName)],
                ["Distributor", plain(organization.distId)],
              ] as [string, string][]
            ).map(([label, value]) => (
              <Group key={label} gap="xs" wrap="nowrap" align="flex-start">
                <Text size="sm" c="dimmed" w={170} style={{ flexShrink: 0 }}>
                  {label}
                </Text>
                <Text size="sm" style={{ wordBreak: "break-word" }}>
                  {value}
                </Text>
              </Group>
            ))}
          </SimpleGrid>
        ) : (
          <Text size="sm" c="dimmed">
            This device is not assigned to an organization, or the lookup failed.
          </Text>
        )}
      </ExplainedSection>

      <ExplainedSection
        title="Device logs"
        description="Log lines the headset app uploaded for this device, newest first."
      >
        {pid ? (
          <DeviceLogs pid={pid} />
        ) : (
          <Text size="sm" c="dimmed">
            No device id available.
          </Text>
        )}
      </ExplainedSection>
    </Stack>
  );
};

export default ProductDetailsPanel;
